/**
 * BusinessDaysScreen — Day Opening & Day Closing
 * API: /api/BusinessDays/api/V1/*
 */
import React, { useState, useEffect, useCallback } from 'react';
import { RefreshCw, Play, Lock, Unlock } from 'lucide-react';
import { erpToast } from '../../components/erp/ERPToast';
import '../../styles/erp-theme.css';
import { API_BASE as API, getToken} from '../../lib/api';

const tkn = () => getToken() || '';
const hdrs = () => ({ Authorization: `Bearer ${tkn()}`, 'Content-Type': 'application/json' });

const MONTHS = ['January','February','March','April','May','June','July','August','September','October','November','December'];

const BusinessDaysScreen = ({ theme = 'light', mode = 'open', onBack }) => {
  const isClose = mode === 'close';
  const [selectedMonth, setSelectedMonth] = useState(() => { const d = new Date(); return `${MONTHS[d.getMonth()]}-${d.getFullYear()}`; });
  const [days, setDays] = useState([]);
  const [loading, setLoading] = useState(false);
  const [password, setPassword] = useState('');
  // Day Close status fields
  const [dayOpenedBy, setDayOpenedBy] = useState('');
  const [dayClosedBy, setDayClosedBy] = useState('');
  const [footfall, setFootfall] = useState('');
  const [holidayRemarks, setHolidayRemarks] = useState('');
  const [dayRemarks, setDayRemarks] = useState('');
  const [businessDates, setBusinessDates] = useState([]);
  const [selectedDay, setSelectedDay] = useState(null);

  const loadDays = useCallback(async () => {
    setLoading(true);
    try {
      // Parse month-year
      const parts = selectedMonth.split('-');
      const monthIdx = MONTHS.indexOf(parts[0]);
      const year = parseInt(parts[1]);
      const fromDate = `01-${String(monthIdx + 1).padStart(2, '0')}-${year}`;
      const lastDay = new Date(year, monthIdx + 1, 0).getDate();
      const toDate = `${lastDay}-${String(monthIdx + 1).padStart(2, '0')}-${year}`;
      
      const r = await fetch(`${API}/api/BusinessDays/api/V1/GetBusinessDaysDetails?FromDate=${fromDate}&ToDate=${toDate}`, { headers: hdrs() });
      const j = await r.json();
      if (j.Success !== false && j.data) setDays(j.data || []);
      else if (Array.isArray(j)) setDays(j);
      else {
        // Generate days locally
        const generated = [];
        for (let d = 1; d <= lastDay; d++) {
          const dt = new Date(year, monthIdx, d);
          generated.push({ SlNo: d, Date: `${String(d).padStart(2,'0')}-${parts[0].substring(0,3)}-${year}`, Day: ['Sunday','Monday','Tuesday','Wednesday','Thursday','Friday','Saturday'][dt.getDay()], Status: 'New', DayOpenedBy: '', PosStatus: 'New' });
        }
        setDays(generated);
      }
    } catch { erpToast.error('Failed to load business days'); }
    setLoading(false);
  }, [selectedMonth]);

  useEffect(() => { loadDays(); }, [loadDays]);

  const handleBegin = async () => {
    if (!selectedDay) { erpToast.warning('Select a day first'); return; }
    try {
      const r = await fetch(`${API}/api/BusinessDays/api/V1/${isClose ? 'EndMonthDetails' : 'StartMonthDetails'}`, { method: 'POST', headers: hdrs(), body: JSON.stringify({ date: selectedDay.Date, password, dayClosedBy, footfall, holidayRemarks, dayRemarks }) });
      const j = await r.json();
      erpToast.success(j.message || `Day ${isClose ? 'closed' : 'opened'} successfully`);
      loadDays();
    } catch { erpToast.error('Operation failed'); }
  };

  // Generate month options
  const monthOptions = [];
  const now = new Date();
  for (let i = -6; i <= 6; i++) {
    const d = new Date(now.getFullYear(), now.getMonth() + i, 1);
    monthOptions.push(`${MONTHS[d.getMonth()]}-${d.getFullYear()}`);
  }

  return (
    <div className="erp-module" data-testid={`business-days-${mode}`}>
      <div className="erp-screen-header">
        {onBack ? (<button onClick={onBack} data-testid="screen-back-btn" style={{display:"flex",alignItems:"center",gap:6,background:"none",border:"none",cursor:"pointer",padding:"2px 0"}}><svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M19 12H5"/><path d="m12 5-7 7 7 7"/></svg><span className="erp-screen-title" style={{color:"var(--erp-accent,#1856a7)"}}>{isClose ? 'Day Closing' : 'Day Opening'}</span></button>) : (<span className="erp-screen-title">{isClose ? 'Day Closing' : 'Day Opening'}</span>)}
        <div className="erp-screen-actions">
          <button className="erp-btn erp-btn-ghost" onClick={loadDays}><RefreshCw size={13} /> Refresh</button>
        </div>
      </div>

      {/* Controls */}
      <div style={{ padding: 'var(--erp-sp-3)', borderBottom: '1px solid var(--erp-border)', display: 'flex', gap: 16, alignItems: 'center', flexWrap: 'wrap' }}>
        <div className="erp-form-row" style={{ margin: 0 }}>
          <label className="erp-form-label">Select Month</label>
          <div className="erp-form-control">
            <select value={selectedMonth} onChange={e => setSelectedMonth(e.target.value)} className="erp-input erp-select" style={{ width: 180 }}>
              {monthOptions.map(m => <option key={m} value={m}>{m}</option>)}
            </select>
          </div>
        </div>
        {!isClose && <button className="erp-btn erp-btn-primary" onClick={handleBegin} data-testid="day-begin-btn"><Play size={13} /> Begin</button>}
        {isClose && <>
          <button className="erp-btn erp-btn-primary" onClick={handleBegin} data-testid="day-close-btn"><Lock size={13} /> Day Close</button>
          <button className="erp-btn erp-btn-ghost" data-testid="day-reopen-btn"><Unlock size={13} /> Day ReOpen</button>
        </>}
      </div>

      {/* Content: Grid + Status + Business Dates */}
      <div style={{ flex: 1, overflow: 'auto', display: 'flex', gap: 8, padding: 'var(--erp-sp-3)' }}>
        {/* Days Grid */}
        <div style={{ flex: 2, overflow: 'auto', border: '1px solid var(--erp-border)', borderRadius: 'var(--erp-radius)' }}>
          <table className="erp-grid" data-testid="days-grid">
            <thead><tr>
              <th style={{ width: 40 }}>Sl.No</th>
              <th style={{ width: 100 }}>Date</th>
              <th style={{ width: 100 }}>Day</th>
              <th style={{ width: 70 }}>Status</th>
              <th style={{ width: 70 }}>Pos Status</th>
              <th>Day Opened By</th>
            </tr></thead>
            <tbody>
              {loading ? <tr><td colSpan={6} style={{ padding: 32, textAlign: 'center' }}>Loading...</td></tr>
              : days.map((d, i) => (
                <tr key={i} className={selectedDay?.SlNo === d.SlNo ? 'selected' : ''} onClick={() => setSelectedDay(d)} style={{ cursor: 'pointer' }}>
                  <td style={{ textAlign: 'center' }}>{d.SlNo || i + 1}</td>
                  <td>{d.Date}</td>
                  <td>{d.Day}</td>
                  <td style={{ fontWeight: 600, color: d.Status === 'Open' ? 'var(--erp-success)' : d.Status === 'Closed' ? 'var(--erp-danger)' : 'var(--erp-text-muted)' }}>{d.Status || 'New'}</td>
                  <td>{d.PosStatus || 'New'}</td>
                  <td>{d.DayOpenedBy || ''}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Status Panel (Day Close only) */}
        {isClose && (
          <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 8 }}>
            <div style={{ border: '1px solid var(--erp-border)', borderRadius: 'var(--erp-radius)', padding: 'var(--erp-sp-3)' }}>
              <div style={{ fontSize: 13, fontWeight: 700, marginBottom: 8 }}>Status</div>
              <div className="erp-form-row" style={{ margin: '4px 0' }}><label className="erp-form-label" style={{ width: 120 }}>Day Opened By</label><div className="erp-form-control"><input value={dayOpenedBy} onChange={e => setDayOpenedBy(e.target.value)} className="erp-input" /></div></div>
              <div className="erp-form-row" style={{ margin: '4px 0' }}><label className="erp-form-label required" style={{ width: 120 }}>Day Closed By</label><div className="erp-form-control"><input value={dayClosedBy} onChange={e => setDayClosedBy(e.target.value)} className="erp-input" /></div></div>
              <div className="erp-form-row" style={{ margin: '4px 0' }}><label className="erp-form-label" style={{ width: 120 }}>No Of FootFall</label><div className="erp-form-control"><input value={footfall} onChange={e => setFootfall(e.target.value)} className="erp-input" type="number" /></div></div>
              <div className="erp-form-row" style={{ margin: '4px 0' }}><label className="erp-form-label" style={{ width: 120 }}>Holiday Remarks</label><div className="erp-form-control"><input value={holidayRemarks} onChange={e => setHolidayRemarks(e.target.value)} className="erp-input" /></div></div>
              <div className="erp-form-row" style={{ margin: '4px 0' }}><label className="erp-form-label" style={{ width: 120 }}>Day Remarks</label><div className="erp-form-control"><input value={dayRemarks} onChange={e => setDayRemarks(e.target.value)} className="erp-input" /></div></div>
            </div>

            {/* Business Dates summary */}
            <div style={{ border: '1px solid var(--erp-border)', borderRadius: 'var(--erp-radius)', padding: 'var(--erp-sp-3)', flex: 1, overflow: 'auto' }}>
              <div style={{ fontSize: 13, fontWeight: 700, marginBottom: 8 }}>Business Dates</div>
              <table className="erp-grid" style={{ fontSize: 11 }}>
                <thead><tr><th>Date</th><th>Open Count</th></tr></thead>
                <tbody>
                  {businessDates.map((bd, i) => <tr key={i}><td>{bd.date}</td><td>{bd.count}</td></tr>)}
                  {businessDates.length === 0 && <tr><td colSpan={2} style={{ textAlign: 'center', color: 'var(--erp-text-muted)' }}>No data</td></tr>}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>

      {/* Password field at bottom */}
      <div style={{ padding: '8px var(--erp-sp-3)', borderTop: '1px solid var(--erp-border)' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <span style={{ fontSize: 12, fontWeight: 600 }}>Enter Your Password</span>
          <input type="password" value={password} onChange={e => setPassword(e.target.value)} className="erp-input" style={{ width: 200 }} data-testid="day-password" />
        </div>
      </div>
    </div>
  );
};

export default BusinessDaysScreen;
